import { useState, useRef, useEffect, useCallback } from "react";
import { useLocale, localePath as localizedPath } from "@/lib/locale";

// ─── Icona femminile con cuffia ──────────────────────────────────────────────
function AgentIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} aria-hidden="true">
      <circle cx="24" cy="17" r="8" fill="currentColor" opacity="0.95" />
      <path d="M14 38c0-5.523 4.477-10 10-10s10 4.477 10 10" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" fill="none" opacity="0.95" />
      <path d="M13 17a11 11 0 0 1 22 0" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" fill="none" />
      <rect x="10" y="15" width="5" height="8" rx="2.5" fill="currentColor" />
      <rect x="33" y="15" width="5" height="8" rx="2.5" fill="currentColor" />
      <path d="M33 21 Q37 25 35 29" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" fill="none" opacity="0.8" />
      <circle cx="34.5" cy="29.5" r="2" fill="currentColor" opacity="0.8" />
    </svg>
  );
}

function SendIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
    </svg>
  );
}

function ShieldIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );
}

// ─── Tipi ────────────────────────────────────────────────────────────────────
interface Message {
  role: "user" | "assistant";
  content: string;
}

type AssistantCopy = {
  greeting: string; consentTitle: string; consentBody: string; consentLegal: string;
  accept: string; assistantLabel: string; status: string; close: string; placeholder: string;
  send: string; open: string; connectionError: string; temporaryError: string; privacy: string;
};
const assistantCopies: Record<"it" | "en" | "de" | "hr", AssistantCopy> = {
  it: {
    greeting: "Ciao! Sono Sofia, l'assistente virtuale di OVVO Camping 👋\nPosso rispondere alle tue domande sul gestionale: funzionalità, prezzi, integrazioni hardware e molto altro. Come posso aiutarti?",
    consentTitle: "Prima di iniziare", consentBody: "Questa chat è gestita dall'assistente AI Sofia (OpenAI GPT-4o-mini). Le conversazioni — incluso il tuo indirizzo IP e le domande inviate — vengono registrate per finalità di assistenza e miglioramento del servizio.", consentLegal: "Base giuridica: consenso (art. 6.1.a GDPR). Puoi revocare il consenso cancellando i dati del sito o contattandoci a", accept: "Accetta e inizia", assistantLabel: "Assistente OVVO", status: "Rispondo subito", close: "Chiudi chat", placeholder: "Scrivi un messaggio…", send: "Invia messaggio", open: "Apri assistente AI OVVO Camping", connectionError: "Connessione interrotta. Chiamaci al +39 0883 1970316 oppure scrivici su WhatsApp al +39 338 298 9310.", temporaryError: "Errore temporaneo. Chiamaci al +39 0883 1970316 oppure scrivici su WhatsApp al +39 338 298 9310.", privacy: "Privacy Policy",
  },
  en: {
    greeting: "Hi! I'm Sofia, OVVO Camping's virtual assistant 👋\nI can answer your questions about the management system: features, pricing, hardware integrations and much more. How can I help?",
    consentTitle: "Before you begin", consentBody: "This chat is managed by Sofia, the AI assistant (OpenAI GPT-4o-mini). Conversations — including your IP address and submitted questions — are recorded to provide assistance and improve the service.", consentLegal: "Legal basis: consent (Art. 6(1)(a) GDPR). You can withdraw consent by deleting the site's data or contacting us at", accept: "Accept and start", assistantLabel: "OVVO Assistant", status: "I'll respond right away", close: "Close chat", placeholder: "Write a message…", send: "Send message", open: "Open OVVO Camping AI assistant", connectionError: "Connection interrupted. Call +39 0883 1970316 or message us on WhatsApp at +39 338 298 9310.", temporaryError: "Temporary error. Call +39 0883 1970316 or message us on WhatsApp at +39 338 298 9310.", privacy: "Privacy Policy",
  },
  de: {
    greeting: "Hallo! Ich bin Sofia, die virtuelle Assistentin von OVVO Camping 👋\nIch beantworte Ihre Fragen zum Verwaltungssystem: Funktionen, Preise, Hardware-Integrationen und vieles mehr. Wie kann ich helfen?",
    consentTitle: "Bevor Sie beginnen", consentBody: "Dieser Chat wird von Sofia, der KI-Assistentin (OpenAI GPT-4o-mini), betreut. Gespräche — einschließlich Ihrer IP-Adresse und gesendeter Fragen — werden zur Unterstützung und Verbesserung des Dienstes aufgezeichnet.", consentLegal: "Rechtsgrundlage: Einwilligung (Art. 6 Abs. 1 lit. a DSGVO). Sie können die Einwilligung widerrufen, indem Sie die Websitedaten löschen oder uns kontaktieren unter", accept: "Akzeptieren und starten", assistantLabel: "OVVO-Assistentin", status: "Ich antworte sofort", close: "Chat schließen", placeholder: "Nachricht schreiben…", send: "Nachricht senden", open: "OVVO Camping KI-Assistent öffnen", connectionError: "Verbindung unterbrochen. Rufen Sie +39 0883 1970316 an oder schreiben Sie uns per WhatsApp an +39 338 298 9310.", temporaryError: "Vorübergehender Fehler. Rufen Sie +39 0883 1970316 an oder schreiben Sie uns per WhatsApp an +39 338 298 9310.", privacy: "Datenschutzerklärung",
  },
  hr: {
    greeting: "Pozdrav! Ja sam Sofia, virtualna asistentica OVVO Campinga 👋\nMogu odgovoriti na vaša pitanja o sustavu upravljanja: funkcionalnostima, cijenama, hardverskim integracijama i još mnogo toga. Kako mogu pomoći?",
    consentTitle: "Prije početka", consentBody: "Ovim chatom upravlja AI asistentica Sofia (OpenAI GPT-4o-mini). Razgovori — uključujući vašu IP adresu i poslana pitanja — bilježe se u svrhu podrške i poboljšanja usluge.", consentLegal: "Pravna osnova: privola (čl. 6. st. 1. t. a GDPR-a). Privolu možete povući brisanjem podataka web-mjesta ili kontaktiranjem na", accept: "Prihvati i započni", assistantLabel: "OVVO asistentica", status: "Odgovaram odmah", close: "Zatvori chat", placeholder: "Napišite poruku…", send: "Pošalji poruku", open: "Otvori AI asistenticu OVVO Campinga", connectionError: "Veza je prekinuta. Nazovite +39 0883 1970316 ili nam pišite na WhatsApp +39 338 298 9310.", temporaryError: "Privremena pogreška. Nazovite +39 0883 1970316 ili nam pišite na WhatsApp +39 338 298 9310.", privacy: "Pravila privatnosti",
  },
};

const CONSENT_KEY = "ovvo_ai_consent";

// ─── Schermata consenso ───────────────────────────────────────────────────────
function ConsentScreen({ onAccept, copy, privacyHref }: { onAccept: () => void; copy: AssistantCopy; privacyHref: string }) {
  return (
    <div className="flex flex-col items-center justify-center h-full px-5 py-6 text-center">
      <div className="w-12 h-12 rounded-full bg-[#1b4332]/10 flex items-center justify-center mb-4">
        <ShieldIcon className="w-6 h-6 text-[#1b4332]" />
      </div>
       <p className="text-sm font-semibold text-gray-800 mb-2">{copy.consentTitle}</p>
      <p className="text-xs text-gray-500 leading-relaxed mb-1">
         {copy.consentBody}
      </p>
      <p className="text-xs text-gray-400 leading-relaxed mb-5">
         {copy.consentLegal}{" "}
        <a href="mailto:info@seosoft.it" className="underline hover:text-gray-600">info@seosoft.it</a>.
         {" "}<a href={privacyHref} className="underline hover:text-gray-600" target="_blank" rel="noopener noreferrer">{copy.privacy}</a>.
      </p>
      <button
        onClick={onAccept}
        className="w-full bg-[#1b4332] hover:bg-[#14532d] text-white text-sm font-semibold py-2.5 px-4 rounded-xl transition-colors"
      >
         {copy.accept}
      </button>
    </div>
  );
}

// ─── Bubble ───────────────────────────────────────────────────────────────────
function Bubble({ msg, isLastAssistant, topRef }: {
  msg: Message;
  isLastAssistant?: boolean;
  topRef?: React.RefObject<HTMLDivElement | null>;
}) {
  const isUser = msg.role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"} mb-3`}>
      {!isUser && (
        <div className="w-7 h-7 rounded-full bg-[#1b4332] flex items-center justify-center shrink-0 mr-2 mt-0.5">
          <AgentIcon className="w-4 h-4 text-white" />
        </div>
      )}
      <div
        ref={isLastAssistant ? (topRef as React.RefObject<HTMLDivElement>) : undefined}
        className={`max-w-[80%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
          isUser
            ? "bg-[#1b4332] text-white rounded-br-sm"
            : "bg-gray-100 text-gray-800 rounded-bl-sm"
        }`}
      >
        {msg.content}
      </div>
    </div>
  );
}

// ─── Indicatore "sta scrivendo" ───────────────────────────────────────────────
function TypingIndicator() {
  return (
    <div className="flex justify-start mb-3">
      <div className="w-7 h-7 rounded-full bg-[#1b4332] flex items-center justify-center shrink-0 mr-2 mt-0.5">
        <AgentIcon className="w-4 h-4 text-white" />
      </div>
      <div className="bg-gray-100 px-4 py-3 rounded-2xl rounded-bl-sm flex items-center gap-1">
        {[0, 1, 2].map((i) => (
          <span key={i} className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: `${i * 0.15}s` }} />
        ))}
      </div>
    </div>
  );
}

// ─── Componente principale ────────────────────────────────────────────────────
export function AiAssistant() {
  const { locale } = useLocale();
  const copy = assistantCopies[locale];
  const [open, setOpen] = useState(false);
  const [consented, setConsented] = useState(() =>
    typeof window !== "undefined" && localStorage.getItem(CONSENT_KEY) === "1"
  );
  const [messages, setMessages] = useState<Message[]>([{ role: "assistant", content: copy.greeting }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showBadge, setShowBadge] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const lastResponseRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Keep the untouched initial greeting in sync when the locale changes.
  useEffect(() => {
    setMessages((current) =>
      current.length === 1 && current[0].role === "assistant"
        ? [{ role: "assistant", content: copy.greeting }]
        : current
    );
  }, [copy.greeting]);

  // Scroll: vai all'inizio della risposta se l'ultimo msg è dell'assistente,
  // altrimenti vai in fondo (durante typing indicator)
  useEffect(() => {
    const last = messages[messages.length - 1];
    if (last?.role === "assistant" && messages.length > 1) {
      // Piccolo ritardo per permettere il render
      setTimeout(() => {
        lastResponseRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 50);
    } else {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  // Durante il caricamento scrolla in fondo (typing indicator)
  useEffect(() => {
    if (loading) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [loading]);

  // Focus sull'input quando si apre
  useEffect(() => {
    if (open) {
      setShowBadge(false);
      if (consented) setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open, consented]);

  const handleConsent = () => {
    localStorage.setItem(CONSENT_KEY, "1");
    setConsented(true);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const send = useCallback(async () => {
    const text = input.trim();
    if (!text || loading) return;

    const userMsg: Message = { role: "user", content: text };
    const updatedMessages = [...messages, userMsg];

    setMessages(updatedMessages);
    setInput("");
    setLoading(true);
    setError(null);

    try {
      const res = await fetch("/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          messages: updatedMessages.map((m) => ({ role: m.role, content: m.content })),
        }),
      });

      const data = (await res.json()) as { ok: boolean; reply?: string; error?: string };

      if (data.ok && data.reply) {
        setMessages((prev) => [...prev, { role: "assistant", content: data.reply! }]);
      } else {
        setError(data.error ?? copy.temporaryError);
      }
    } catch {
      setError(copy.connectionError);
    } finally {
      setLoading(false);
    }
  }, [input, loading, messages, copy]);

  const handleKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  // Trova l'indice dell'ultimo messaggio dell'assistente
  const lastAssistantIdx = messages.reduce((acc, m, i) => (m.role === "assistant" ? i : acc), -1);

  return (
    <>
      {/* ── Pannello chat ─────────────────────────────────────────── */}
      <div
        className={`fixed inset-x-3 bottom-[calc(max(0.5rem,env(safe-area-inset-bottom))+3.25rem)] z-50 flex max-h-[calc(100dvh-5.5rem)] flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl transition-all duration-300 origin-bottom-right sm:inset-x-auto sm:bottom-24 sm:right-6 sm:w-[380px] ${
          open ? "opacity-100 scale-100 pointer-events-auto" : "opacity-0 scale-95 pointer-events-none"
        }`}
        style={{ height: "min(520px, calc(100dvh - 6.5rem))" }}
        role="dialog"
        aria-label={copy.assistantLabel}
        data-testid="dialog-ai-assistant"
      >
        {/* Header */}
        <div className="bg-[#1b4332] px-4 py-3 flex items-center gap-3 shrink-0">
          <div className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center shrink-0">
            <AgentIcon className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1 min-w-0">
             <p className="text-white font-semibold text-sm leading-tight">Sofia — {copy.assistantLabel}</p>
             <p className="text-white/70 text-xs">{copy.status}</p>
          </div>
          <button
            onClick={() => setOpen(false)}
             aria-label={copy.close}
            className="text-white/70 hover:text-white transition-colors p-1 rounded"
          >
            <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
            </svg>
          </button>
        </div>

        {/* Contenuto: consenso o chat */}
        {!consented ? (
           <ConsentScreen onAccept={handleConsent} copy={copy} privacyHref={localizedPath("/privacy", locale)} />
        ) : (
          <>
            {/* Messaggi */}
            <div className="flex-1 overflow-y-auto px-4 py-4" style={{ minHeight: 0 }}>
              {messages.map((msg, i) => (
                <Bubble
                  key={i}
                  msg={msg}
                  isLastAssistant={i === lastAssistantIdx}
                  topRef={i === lastAssistantIdx ? lastResponseRef : undefined}
                />
              ))}
              {loading && <TypingIndicator />}
              {error && (
                <div className="text-xs text-red-600 bg-red-50 rounded-xl px-3 py-2 mb-2">{error}</div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="border-t border-gray-100 px-3 py-3 flex items-end gap-2 bg-white shrink-0">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKey}
                 placeholder={copy.placeholder}
                rows={1}
                maxLength={500}
                disabled={loading}
                className="flex-1 resize-none text-sm text-gray-800 placeholder-gray-400 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#1b4332]/30 focus:border-[#1b4332] transition-colors disabled:opacity-50"
                style={{ maxHeight: "100px", overflowY: "auto" }}
                onInput={(e) => {
                  const el = e.currentTarget;
                  el.style.height = "auto";
                  el.style.height = Math.min(el.scrollHeight, 100) + "px";
                }}
              />
              <button
                onClick={send}
                disabled={loading || !input.trim()}
                 aria-label={copy.send}
                className="w-9 h-9 rounded-xl bg-[#1b4332] text-white flex items-center justify-center shrink-0 hover:bg-[#14532d] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <SendIcon className="w-4 h-4" />
              </button>
            </div>
          </>
        )}
      </div>

      {/* ── Pulsante flottante ───────────────────────────────────────── */}
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label={copy.open}
        data-testid="button-ai-assistant"
        className="fixed bottom-[max(0.5rem,env(safe-area-inset-bottom))] right-2 z-50 flex h-11 w-[calc(50%-0.625rem)] items-center justify-center gap-1.5 rounded-xl bg-[#1b4332]/95 px-2.5 text-white shadow-md transition-all duration-300 hover:bg-[#14532d] md:bottom-6 md:right-6 md:h-14 md:w-14 md:rounded-full md:bg-[#1b4332] md:px-0 md:shadow-2xl md:shadow-green-900/30 md:hover:-translate-y-1 md:hover:shadow-green-900/50"
      >
        {showBadge && !open && (
          <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-[#f59e0b] border-2 border-white animate-pulse" />
        )}
        {open ? (
          <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
          </svg>
        ) : (
          <AgentIcon className="h-5 w-5 md:h-8 md:w-8" />
        )}
        {!open && <span className="truncate text-xs font-semibold md:hidden">{copy.assistantLabel}</span>}
      </button>
    </>
  );
}
